import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, X, Play, Pause, Volume2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HTTP_FORBIDDEN = 403;

const ChatView = ({ user }) => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [icebreakers, setIcebreakers] = useState([]);
  const [loadingIcebreakers, setLoadingIcebreakers] = useState(false);
  const [partner, setPartner] = useState(null);
  const [bioPlaying, setBioPlaying] = useState(false);
  const bioAudioRef = useRef(null);
  const messagesEndRef = useRef(null);

  const isPremium = user?.user?.subscription_status === 'premium';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/${matchId}/messages`, {
        withCredentials: true
      });
      setMessages(response.data.messages);
      setLoading(false);
    } catch (error) {
      console.error('Fetch messages error:', error);
      if (error.response?.status === HTTP_FORBIDDEN) {
        toast.error('Premium subscription required');
        navigate('/upgrade');
      }
      setLoading(false);
    }
  }, [matchId, navigate]);

  useEffect(() => {
    if (!isPremium) {
      toast.error('Premium subscription required');
      navigate('/upgrade');
      return;
    }
    fetchMessages();
    axios
      .get(`${API}/chat/${matchId}/partner`, { withCredentials: true })
      .then((res) => setPartner(res.data.partner))
      .catch(() => {});
    const interval = setInterval(fetchMessages, 3000); // Poll for new messages
    return () => clearInterval(interval);
  }, [isPremium, fetchMessages, navigate, matchId]);

  const toggleBioAudio = () => {
    if (!bioAudioRef.current) return;
    if (bioPlaying) {
      bioAudioRef.current.pause();
      setBioPlaying(false);
    } else {
      bioAudioRef.current.play();
      setBioPlaying(true);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageText = newMessage;
    setNewMessage(''); // Clear input immediately

    try {
      const response = await axios.post(
        `${API}/chat/${matchId}/send`,
        { message_text: messageText },
        { withCredentials: true }
      );

      if (response.data.success) {
        setMessages([...messages, response.data.message]);
        setIcebreakers([]);
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Failed to send message');
      setNewMessage(messageText); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleGetIcebreakers = async () => {
    if (loadingIcebreakers) return;
    setLoadingIcebreakers(true);
    try {
      const response = await axios.get(`${API}/chat/${matchId}/icebreakers`, {
        withCredentials: true
      });
      setIcebreakers(response.data.icebreakers || []);
      if (!response.data.icebreakers?.length) {
        toast.info('No suggestions available right now');
      }
    } catch (error) {
      console.error('Icebreaker error:', error);
      toast.error('Could not generate icebreakers');
    } finally {
      setLoadingIcebreakers(false);
    }
  };

  const useIcebreaker = (text) => {
    setNewMessage(text);
    setIcebreakers([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="glass border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/matches')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center space-x-3">
              <div className="relative w-10 h-10 flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold">
                  {partner?.name?.[0]?.toUpperCase() || '?'}
                </div>
                {partner?.profile_photo && (
                  <img
                    src={partner.profile_photo}
                    alt={partner.name}
                    className="absolute inset-0 w-10 h-10 rounded-full object-cover"
                  />
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900">{partner?.name || 'Chat'}</h2>
            </div>
            {partner?.voice_bio?.audio_data && (
              <button
                onClick={toggleBioAudio}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 hover:from-pink-200 hover:to-purple-200 text-pink-600 font-medium text-sm transition-all"
                data-testid="replay-voice-bio-btn"
              >
                {bioPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                <Volume2 className="w-4 h-4" />
                <span className="hidden sm:inline">{bioPlaying ? 'Playing bio...' : 'Voice Bio'}</span>
              </button>
            )}
            {partner?.voice_bio?.audio_data && (
              <audio
                ref={bioAudioRef}
                src={partner.voice_bio.audio_data}
                onEnded={() => setBioPlaying(false)}
                className="hidden"
              />
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender_id === user?.user?.id ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.id}`}
              >
                <div
                  className={`chat-bubble ${
                    message.sender_id === user?.user?.id
                      ? 'chat-bubble-sent'
                      : 'chat-bubble-received'
                  }`}
                >
                  <p className="text-sm">{message.message_text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(message.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <footer className="glass border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* AI Icebreaker suggestions */}
          {icebreakers.length > 0 && (
            <div className="mb-3 space-y-2" data-testid="icebreaker-suggestions">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-purple-600 flex items-center">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> AI Icebreakers
                </span>
                <button
                  onClick={() => setIcebreakers([])}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="dismiss-icebreakers-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {icebreakers.map((text, idx) => (
                <button
                  key={text}
                  onClick={() => useIcebreaker(text)}
                  className="w-full text-left text-sm bg-white border border-purple-200 hover:border-purple-400 hover:bg-purple-50 rounded-xl px-4 py-2.5 transition-all"
                  data-testid={`icebreaker-option-${idx}`}
                >
                  {text}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleGetIcebreakers}
              disabled={loadingIcebreakers}
              title="Suggest an opener"
              className="w-12 h-12 flex-shrink-0 rounded-full border-2 border-purple-200 text-purple-500 hover:bg-purple-50 flex items-center justify-center transition-all disabled:opacity-50"
              data-testid="icebreaker-btn"
            >
              {loadingIcebreakers ? (
                <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-gray-200 focus:border-pink-500 focus:outline-none transition-colors"
              disabled={sending}
              data-testid="message-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </footer>
    </div>
  );
};

export default ChatView;